import React, { useEffect, useMemo, useState } from 'react'

import { useStore } from '@nanostores/react'
import { PackageCard } from './PackageCard'
import {
  getDaysOfFoodArrayFromSku,
  getMealsArrayFromSku,
} from '@/utilities/sakara'
import type { Package } from '@/models/package'
import { $packages } from '@/stores/packages/stores'
import { loadPackages } from '@/stores/packages/operations'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'

import 'swiper/scss'
import 'swiper/scss/pagination'
import './Packages.scss'

const DEFAULT_NUMBER_OF_PACKAGES_DISPLAYED = 4

type PackagesProps = {
  homePage?: boolean
}

export const Packages: React.FC<PackagesProps> = ({ homePage = false }) => {
  const packages = useStore($packages)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [loading, setLoading] = useState(true)

  const fetchPackages = async (
    page: number,
    perPage: number,
    force = false,
  ) => {
    setLoading(true)

    await loadPackages({
      page: page,
      perPage: perPage,
      force: force,
    })

    setLoading(false)
  }

  const loadMorePackages = () => {
    setCurrentPage((prevPage) => prevPage + 1)
  }

  const getMeals = (sku: string) => {
    return getMealsArrayFromSku(sku)
      ?.map((meal) => meal.charAt(0).toUpperCase() + meal.slice(1))
      .join(' + ')
  }

  const getDays = (sku: string) => {
    return getDaysOfFoodArrayFromSku(sku)
      .map((meal) => meal.charAt(0).toUpperCase() + meal.slice(1))
      .join(' - ')
  }

  const handleManagePackage = (pack: Package) => {
    window.location.href = `/packages?package_id=${pack.id}`
  }

  const handlePackageHistory = (pack: Package) => {
    window.location.href = `/packages?package_id=${pack.id}`
  }

  const createPackageButton = (pack: Package) => {
    return pack.isActive ? (
      <button
        onClick={() => handleManagePackage(pack)}
        className="button button--secondary button--small"
      >
        Manage Package
      </button>
    ) : (
      <button
        onClick={() => handlePackageHistory(pack)}
        className="button button--secondary button--small"
      >
        Package History
      </button>
    )
  }

  useEffect(() => {
    const fetchExtraSubscriptions = async () => {
      await fetchPackages(currentPage, DEFAULT_NUMBER_OF_PACKAGES_DISPLAYED)
      await fetchPackages(currentPage + 1, DEFAULT_NUMBER_OF_PACKAGES_DISPLAYED)
    }

    fetchExtraSubscriptions()
  }, [currentPage])

  const displayedActivePackages = useMemo(() => {
    if (homePage) {
      return packages.slice(
        0,
        currentPage * DEFAULT_NUMBER_OF_PACKAGES_DISPLAYED - 1,
      )
    }
    return packages.slice(0, currentPage * DEFAULT_NUMBER_OF_PACKAGES_DISPLAYED)
  }, [currentPage, packages])

  const showLoadMoreButton = packages.length > displayedActivePackages.length

  if (!packages.length && !loading)
    if (!homePage) {
      return (
        <div className="account-packages">
          <div className="type-h4">No Active Packages</div>
          <p className="type-body account-packages__no-subscriptions-text">
            You currently have no packages. Click on the link below to discover
            our nutrition programs, powered by whole-food ingredients.
          </p>
          <button
            className="button button--primary"
            onClick={() => {
              window.location.href = '/nutrition/signature-nutrition-program'
            }}
          >
            Order Now
          </button>
        </div>
      )
    } else {
      return
    }

  return (
    <div className={`${homePage ? 'packages-homepage' : 'account-packages'}`}>
      {homePage && (
        <h2 className="account-packages__header type-label">Packs Of Weeks</h2>
      )}
      <div className="account-packages__list">
        <div className="account-packages__list-wrapper">
          {displayedActivePackages.map((pack) => {
            const buttons = createPackageButton(pack)
            return (
              <div key={pack.id}>
                <PackageCard
                  key={pack.id}
                  title="Signature Nutrition Pack Of Weeks"
                  info={getMeals(pack.sku)}
                  body={getDays(pack.sku)}
                  notice={`${pack.numRedemptionsRemaining}/${pack.numWeeks} weeks remaining`}
                  buttons={buttons}
                  isActive={pack.isActive}
                  homepage={homePage}
                />
              </div>
            )
          })}
          {showLoadMoreButton && !homePage && (
            <button
              className={`account-packages__load-more button button--secondary button--small o-loadable o-loadable--small o-loadable--inverted ${
                loading ? 'is-loading' : ''
              }`}
              disabled={loading}
              onClick={loadMorePackages}
            >
              Show More
            </button>
          )}
        </div>
        {homePage && packages.length > displayedActivePackages.length && (
          <button
            className={`packages__load-more button button--primary
              }`}
            onClick={() => {
              window.location.href = 'account?tab_id=packs-of-weeks'
            }}
          >
            Manage Packages
          </button>
        )}
      </div>
      {homePage && (
        <div>
          <Swiper
            className="swiper-container"
            modules={[Pagination]}
            spaceBetween={32}
            slidesPerView={1}
            pagination={{
              el: '.packages-homepage__swiper-pagination',
              clickable: true,
            }}
          >
            {displayedActivePackages.map((pack, index) => {
              const buttons = createPackageButton(pack)
              return (
                <SwiperSlide key={index} className="swiper-slide">
                  <div>
                    <PackageCard
                      title="Signature Nutrition Pack Of Weeks"
                      info={getMeals(pack.sku)}
                      body={getDays(pack.sku)}
                      notice={`${pack.numRedemptionsRemaining}/${pack.numWeeks} weeks remaining`}
                      buttons={buttons}
                      isActive={pack.isActive}
                      homepage={homePage}
                    />
                  </div>
                </SwiperSlide>
              )
            })}
            {packages.length >= DEFAULT_NUMBER_OF_PACKAGES_DISPLAYED && (
              <SwiperSlide className="swiper-slide swiper-slide-end">
                <button
                  className={`packages__load-more button button--primary
                      }`}
                  onClick={() => {
                    window.location.href = 'account?tab_id=packs-of-weeks'
                  }}
                >
                  Manage Packages
                </button>
              </SwiperSlide>
            )}
          </Swiper>
          <div className="swiper-pagination packages-homepage__swiper-pagination" />
        </div>
      )}
    </div>
  )
}
